import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { ArrowLeft, Loader2, Lock, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

export const ResetPasswordPage = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const navigate = useNavigate();
    
    const [loading, setLoading] = useState(true);
    const [verifying, setVerifying] = useState(true);
    const [tokenValid, setTokenValid] = useState(false);
    const [email, setEmail] = useState('');
    const [error, setError] = useState('');
    
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);

    // Verify token on mount
    useEffect(() => {
        if (!token) {
            setError('No reset token provided');
            setVerifying(false);
            setLoading(false);
            return;
        }
        
        const verifyToken = async () => {
            try {
                const response = await authAPI.verifyResetToken(token);
                setTokenValid(true);
                setEmail(response.data.email);
            } catch (err) {
                setError(err.response?.data?.detail || 'Invalid or expired reset link');
                setTokenValid(false);
            } finally {
                setVerifying(false);
                setLoading(false);
            }
        };
        
        verifyToken();
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        
        if (newPassword !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }
        
        if (newPassword.length < 6) {
            setError('Password must be at least 6 characters');
            return;
        }
        
        setSubmitting(true);
        try {
            const response = await authAPI.resetPassword(token, newPassword);
            
            // Store the token and user data
            localStorage.setItem('auth_token', response.data.access_token);
            localStorage.setItem('user', JSON.stringify(response.data.user));
            
            setSuccess(true);
            toast.success('Password reset successful!');
            
            // Redirect to library after 2 seconds
            setTimeout(() => {
                navigate('/my-library');
            }, 2000);
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to reset password');
        } finally {
            setSubmitting(false);
        }
    };

    // Loading state
    if (loading || verifying) {
        return (
            <div className="min-h-[calc(100vh-64px)] flex items-center justify-center" data-testid="reset-password-page">
                <Loader2 className="h-8 w-8 animate-spin text-accent" />
            </div>
        );
    }

    // Success state
    if (success) {
        return (
            <div className="min-h-[calc(100vh-64px)] flex" data-testid="reset-password-page">
                <div className="flex-1 flex items-center justify-center p-8">
                    <div className="w-full max-w-md">
                        <Card className="border-border/60">
                            <CardHeader className="space-y-1 text-center">
                                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                                    <CheckCircle className="h-8 w-8 text-green-600" />
                                </div>
                                <CardTitle className="font-serif text-2xl">Password Reset Complete</CardTitle>
                                <CardDescription>
                                    Your password has been updated successfully.
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <p className="text-center text-sm text-muted-foreground mb-4">
                                    Redirecting you to your library...
                                </p>
                                <div className="flex justify-center">
                                    <Loader2 className="h-5 w-5 animate-spin text-accent" />
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>

                <div className="hidden lg:block w-1/2 relative">
                    <img
                        src="https://images.unsplash.com/photo-1752649937956-0859c4446377?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                        alt="Artist studio"
                        className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-background/50 to-transparent" />
                </div>
            </div>
        );
    }

    // Invalid token state
    if (!tokenValid) {
        return (
            <div className="min-h-[calc(100vh-64px)] flex" data-testid="reset-password-page">
                <div className="flex-1 flex items-center justify-center p-8">
                    <div className="w-full max-w-md">
                        <Link 
                            to="/" 
                            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8"
                        >
                            <ArrowLeft className="h-4 w-4" />
                            Back to Home
                        </Link>

                        <Card className="border-border/60">
                            <CardHeader className="space-y-1 text-center">
                                <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                                    <AlertCircle className="h-8 w-8 text-red-600" />
                                </div>
                                <CardTitle className="font-serif text-2xl">Invalid Reset Link</CardTitle>
                                <CardDescription>
                                    {error || 'This password reset link is invalid or has expired.'}
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <p className="text-center text-sm text-muted-foreground">
                                    Please request a new password reset link.
                                </p>
                                <Button
                                    asChild
                                    className="w-full bg-accent text-accent-foreground hover:bg-accent/90"
                                >
                                    <Link to="/forgot-password">Request New Link</Link>
                                </Button>
                                <Button
                                    asChild
                                    variant="outline"
                                    className="w-full"
                                >
                                    <Link to="/login">Back to Login</Link>
                                </Button>
                            </CardContent>
                        </Card>
                    </div>
                </div>

                <div className="hidden lg:block w-1/2 relative">
                    <img
                        src="https://images.unsplash.com/photo-1752649937956-0859c4446377?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                        alt="Artist studio"
                        className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-background/50 to-transparent" />
                </div>
            </div>
        );
    }

    // Reset password form
    return (
        <div className="min-h-[calc(100vh-64px)] flex" data-testid="reset-password-page">
            <div className="flex-1 flex items-center justify-center p-8">
                <div className="w-full max-w-md">
                    <Link 
                        to="/" 
                        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Back to Home
                    </Link>

                    <Card className="border-border/60">
                        <CardHeader className="space-y-1 text-center pb-4">
                            <div className="w-16 h-16 rounded-full bg-orange-100 flex items-center justify-center mx-auto mb-4">
                                <Lock className="h-8 w-8 text-accent" />
                            </div>
                            <CardTitle id="reset-password-title" className="font-serif text-2xl">
                                Create New Password
                            </CardTitle>
                            <CardDescription>
                                Enter a new password for <strong>{email}</strong>
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <form onSubmit={handleSubmit} className="space-y-4" aria-labelledby="reset-password-title">
                                {error && (
                                    <div 
                                        className="p-3 rounded-md bg-destructive/10 text-destructive text-sm" 
                                        data-testid="reset-error"
                                        role="alert"
                                        aria-live="polite"
                                    >
                                        {error}
                                    </div>
                                )}

                                <div className="space-y-2">
                                    <Label htmlFor="newPassword">New Password</Label>
                                    <Input
                                        id="newPassword"
                                        type="password"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        placeholder="Enter new password (min 6 characters)"
                                        required
                                        minLength={6}
                                        data-testid="new-password-input"
                                        aria-describedby="password-requirements"
                                        autoComplete="new-password"
                                    />
                                    <p id="password-requirements" className="sr-only">
                                        Password must be at least 6 characters long
                                    </p>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="confirmPassword">Confirm Password</Label>
                                    <Input
                                        id="confirmPassword"
                                        type="password"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        placeholder="Confirm new password"
                                        required
                                        data-testid="confirm-password-input"
                                        autoComplete="new-password"
                                    />
                                </div>

                                <Button
                                    type="submit"
                                    className="w-full bg-accent text-accent-foreground hover:bg-accent/90"
                                    disabled={submitting}
                                    data-testid="reset-password-btn"
                                    aria-busy={submitting}
                                >
                                    {submitting ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                                            <span>Resetting Password...</span>
                                        </>
                                    ) : (
                                        'Reset Password'
                                    )}
                                </Button>
                            </form>
                        </CardContent>
                    </Card>
                </div>
            </div>

            <div className="hidden lg:block w-1/2 relative">
                <img
                    src="https://images.unsplash.com/photo-1752649937956-0859c4446377?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                    alt="Artist studio"
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-background/50 to-transparent" />
            </div>
        </div>
    );
};

export default ResetPasswordPage;
